
export interface ISearchStringModel {
    searchStrings: Record<string, string>;
}

export interface IOrderingModel<TOrderId> {
    orderBy?: OrderColumn<TOrderId>[];
}

export interface OrderColumn<TOrderId> {
    id: TOrderId;
    desc?: boolean;
}

export interface IFilteringModel<TFilterKey> {
    filterBy: FilterColumn<TFilterKey>[];
}

export interface FilterColumn<TFilterKey> {
    key: TFilterKey;
    values?: any[];
}

export const createSearchStringConverter = (
    searchStrings: Record<string, string> | undefined,
    mapping: Record<string, string>
): Record<string, string> | undefined => {
    const resolver = createRecordResolver(mapping);
    return resolver(searchStrings);
};

export const createSortOrderConverter = <TOrderId>(
    sortOrders: [string, 'asc' | 'desc'][] | undefined,
    mapping: Record<string, TOrderId>
): OrderColumn<TOrderId>[] | undefined => {
    if (!sortOrders || sortOrders.length === 0) {
        return undefined;
    }

    const resolver = createResolver(mapping);

    return sortOrders.map(([property, direction]) => ({
        id: resolver(property),
        desc: direction === 'desc'
    }));
};

export const createFilterByConverter = <TFilterKey extends string>(
    filterValues: Record<string, any> | undefined
): FilterColumn<TFilterKey>[] | undefined => {
    if (!filterValues) {
        return undefined;
    }

    return Object.entries(filterValues)
        .filter(([, value]) => value != null)
        .map(([key, value]) => ({
            key: key as TFilterKey,
            values: Array.isArray(value) ? value : [value]
        }));
};

const createResolver = <T>(mapping: Record<string, T>) => {
    return (input: string): T => {
        if (!(input in mapping)) {
            throw new Error(`Unknown mapping: ${input}`);
        }
        return mapping[input];
    };
};

const createRecordResolver = (mapping: Record<string, string>) => {
    return (input: Record<string, string> | undefined): Record<string, string> | undefined => {
        if (!input) {
            return undefined;
        }
        const result: Record<string, string> = {};
        for (const key in input) {
            const mappedKey = mapping[key] ?? key;
            result[mappedKey] = input[key];
        }
        return result;
    };
};
